import React from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import CheckCircleOutline from "@mui/icons-material/CheckCircleOutline";
import RadioButtonUnchecked from "@mui/icons-material/RadioButtonUnchecked";
import Autorenew from "@mui/icons-material/Autorenew";
import ErrorOutline from "@mui/icons-material/ErrorOutline";
import { useTranslation } from "react-i18next";

const PHASE_ORDER = ["extract", "send", "ai", "save"];

function phaseIndex(phase) {
  if (!phase || phase === "failed") return -1;
  if (phase === "extracting" || phase === "uploading") return 0;
  if (phase === "sending" || phase === "queued" || phase === "sync") return 1;
  if (phase === "running") return 2;
  if (phase === "saving") return 3;
  if (phase === "completed") return 4;
  return 1;
}

function StepIcon({ done, active, failed }) {
  if (failed) return <ErrorOutline color="error" fontSize="small" />;
  if (done) return <CheckCircleOutline color="success" fontSize="small" />;
  if (active) return <Autorenew color="primary" fontSize="small" sx={{ animation: "spin 1.2s linear infinite", "@keyframes spin": { "0%": { transform: "rotate(0deg)" }, "100%": { transform: "rotate(360deg)" } } }} />;
  return <RadioButtonUnchecked color="disabled" fontSize="small" />;
}

export default function ChatbotFlowImportProgress({ progress, onClose }) {
  const { t } = useTranslation("settings");
  if (!progress?.open) return null;

  const { phase, fileName, page, totalPages, serverStatus, logs = [], error } = progress;
  const idx = phaseIndex(phase);
  const isDone = phase === "completed";
  const isFailed = phase === "failed";
  const inProgress = !isDone && !isFailed;

  const steps = [
    {
      key: "extract",
      label: t("chatbotConfigPage.flowImportStepExtract"),
      detail:
        phase === "extracting" && totalPages > 0
          ? t("chatbotConfigPage.flowImportStepExtractDetail", { page, total: totalPages })
          : phase === "uploading"
            ? t("chatbotConfigPage.flowImportStepUpload")
            : null,
    },
    {
      key: "send",
      label: t("chatbotConfigPage.flowImportStepSend"),
      detail:
        phase === "queued"
          ? t("chatbotConfigPage.flowImportStatusQueued")
          : phase === "sending" || phase === "sync"
            ? t("chatbotConfigPage.flowImportStepSendDetail")
            : null,
    },
    {
      key: "ai",
      label: t("chatbotConfigPage.flowImportStepAi"),
      detail:
        phase === "running"
          ? t("chatbotConfigPage.flowImportStatusRunning")
          : serverStatus && phase !== "completed"
            ? serverStatus
            : null,
    },
    {
      key: "save",
      label: t("chatbotConfigPage.flowImportStepSave"),
      detail:
        phase === "saving"
          ? t("chatbotConfigPage.flowImportStatusSaving")
          : isDone
            ? t("chatbotConfigPage.flowImportStepDone")
            : null,
    },
  ];

  return (
    <Dialog
      open
      onClose={inProgress ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={inProgress}
    >
      <DialogTitle>{t("chatbotConfigPage.flowImportProgressTitle")}</DialogTitle>
      <DialogContent dividers>
        {fileName && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t("chatbotConfigPage.flowImportProgressFile", { name: fileName })}
          </Typography>
        )}

        {inProgress && <LinearProgress sx={{ mb: 2 }} />}

        {isFailed && (
          <Box sx={{ mb: 2, p: 1.5, bgcolor: "error.light", borderRadius: 1, color: "error.contrastText" }}>
            <Typography variant="body2">{error || t("chatbotConfigPage.flowImportPdfError")}</Typography>
          </Box>
        )}

        {isDone && (
          <Box sx={{ mb: 2, p: 1.5, bgcolor: "success.light", borderRadius: 1 }}>
            <Typography variant="body2" color="success.dark">
              {t("chatbotConfigPage.flowImportPdfSuccess")}
            </Typography>
          </Box>
        )}

        <List dense disablePadding>
          {steps.map((step, i) => {
            const stepIdx = PHASE_ORDER.indexOf(step.key);
            const done = isDone || idx > stepIdx;
            const active = !isDone && !isFailed && idx === stepIdx;
            const failed = isFailed && idx === stepIdx;
            return (
              <ListItem key={step.key} disableGutters sx={{ py: 0.75 }}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <StepIcon done={done} active={active} failed={failed} />
                </ListItemIcon>
                <ListItemText
                  primary={step.label}
                  secondary={step.detail}
                  primaryTypographyProps={{ fontWeight: active ? 600 : 400 }}
                />
              </ListItem>
            );
          })}
        </List>

        {logs.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
              {t("chatbotConfigPage.flowImportServerLog")}
            </Typography>
            <Box
              sx={{
                maxHeight: 120,
                overflow: "auto",
                bgcolor: "action.hover",
                borderRadius: 1,
                p: 1,
                fontFamily: "monospace",
                fontSize: 11,
              }}
            >
              {logs.slice(-8).map((line, i) => (
                <div key={`${i}-${line}`}>{line}</div>
              ))}
            </Box>
          </Box>
        )}

        {inProgress && (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 2 }}>
            {t("chatbotConfigPage.flowImportProgressWait")}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={inProgress} variant={isDone || isFailed ? "contained" : "text"}>
          {t("chatbotConfigPage.flowImportProgressClose")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
